import { v4 as uuidv4 } from 'uuid';

import { areAllFlightAirportsGstApplicable } from '~/utils/businessTravellerGstAirports';

const SERVICE_FEE_CALL_CENTRE_SOURCES: Array<App.ServiceFeeSourceEnum> = ['CALL_CENTRE', 'AFTER_HOURS_CALL_CENTRE'];

export interface CustomOfferServiceFeeOptions {
  country?: string;
  flightLegs?: Array<App.CustomOfferFlightLeg>;
  flightType?: string;
}

export interface CustomOfferServiceFeeDropdownOption {
  source: App.ServiceFeeSourceEnum;
  type: App.ServiceFeeTypeEnum;
  destination?: App.ServiceFeeDestinationEnum;
  product?: App.ServiceFeeProductEnum;
  conciergeMode: boolean;
  amount: number;
  /** "$25 - call_centre, booking, international, hotels" */
  label: string;
  isNoServiceFee?: boolean;
  isCustomServiceFee?: boolean;
}

export const NO_SERVICE_FEE_DROPDOWN_OPTION: CustomOfferServiceFeeDropdownOption = {
  source: 'CALL_CENTRE',
  type: 'BOOKING',
  conciergeMode: false,
  amount: 0,
  label: '$0 - no service fee',
  isNoServiceFee: true,
};

// Same persisted shape as the no-fee row; only the amount differs and is entered by the admin.
export const CUSTOM_SERVICE_FEE_DROPDOWN_OPTION: CustomOfferServiceFeeDropdownOption = {
  source: 'CALL_CENTRE',
  type: 'BOOKING',
  conciergeMode: false,
  amount: 0,
  label: 'Custom service fee',
  isCustomServiceFee: true,
};

/**
 * Builds the dropdown options shown in the LEBT custom-offer service fee picker.
 *
 * Filtering rules:
 *  - Source must be CALL_CENTRE or AFTER_HOURS_CALL_CENTRE (online fees excluded — admin booking is not online).
 *  - No filter on `type` — fees are reused across BOOKING and amendment flows.
 *  - Destination is derived from the offer:
 *      * non-flight items → from `country` (Australia ⇒ DOMESTIC, otherwise INTERNATIONAL)
 *      * flight items → from segment airports (all Australian ⇒ DOMESTIC, otherwise INTERNATIONAL)
 *      * if destination can't be determined yet, all destination variants are included.
 *  - Product is derived from the offer:
 *      * hotel → HOTELS or unspecified
 *      * one-way / return flight → SINGLE_RETURN_FLIGHTS or unspecified
 *      * multi-city flight → MULTI_CITY_FLIGHTS or unspecified
 *      * other types → only unspecified-product fees
 *
 * Each matching fee row produces up to two options: regular `amount` and `conciergeAmount`.
 * Zero-value variants are skipped, except the explicit no-service-fee option.
 */
export function getCustomOfferServiceFeeOptions(
  fees: Array<App.BusinessServiceFee>,
  itemType: App.CustomOfferType,
  options: CustomOfferServiceFeeOptions = {},
): Array<CustomOfferServiceFeeDropdownOption> {
  const destination = deriveDestination(itemType, options);
  const allowedProducts = deriveAllowedProducts(itemType, options.flightType);

  const filteredFeeOptions = fees
    .filter((fee) => SERVICE_FEE_CALL_CENTRE_SOURCES.includes(fee.source))
    .filter((fee) => !destination || !fee.destination || fee.destination === destination)
    .filter((fee) => !allowedProducts || allowedProducts.includes(fee.product))
    .flatMap((fee) => [
      fee.amount > 0 ? buildDropdownOption({ fee, conciergeMode: false }) : null,
      fee.conciergeAmount > 0 ? buildDropdownOption({ fee, conciergeMode: true }) : null,
    ])
    .filter((option): option is CustomOfferServiceFeeDropdownOption => option !== null);

  return [CUSTOM_SERVICE_FEE_DROPDOWN_OPTION, NO_SERVICE_FEE_DROPDOWN_OPTION, ...filteredFeeOptions];
}

export function deriveDestination(
  itemType: App.CustomOfferType,
  options: CustomOfferServiceFeeOptions,
): App.ServiceFeeDestinationEnum | undefined {
  if (itemType === 'flight') {
    if (!options.flightLegs?.length) {
      return undefined;
    }
    return areAllFlightAirportsGstApplicable(options.flightLegs) ? 'DOMESTIC' : 'INTERNATIONAL';
  }

  const country = options.country?.trim();
  if (!country) {
    return undefined;
  }
  return country.toLowerCase() === 'australia' ? 'DOMESTIC' : 'INTERNATIONAL';
}

/**
 * Returns the list of `product` values a fee may have to be considered applicable for the offer,
 * or `undefined` to skip product filtering entirely.
 *
 * `undefined` inside the returned list represents fee rows with no specific product set
 * (the "blank" product the ticket refers to).
 */
export function deriveAllowedProducts(
  itemType: App.CustomOfferType,
  flightType?: string,
): Array<App.ServiceFeeProductEnum | undefined> | undefined {
  switch (itemType) {
    case 'hotel':
      return ['HOTELS', undefined];
    case 'flight':
      if (flightType === 'multi-city') {
        return ['MULTI_CITY_FLIGHTS', undefined];
      }
      // 'one-way', 'return', or unset — default to single/return
      return ['SINGLE_RETURN_FLIGHTS', undefined];
    default:
      // experience, other, cruise, tour, ics_store, le_live, custom-offer-package — only blank-product fees
      return [undefined];
  }
}

export function formatServiceFeeLabel(
  fee: Pick<App.BusinessServiceFee, 'source' | 'type' | 'destination' | 'product'>,
  amount: number,
  conciergeMode: boolean,
): string {
  const parts: Array<string> = [
    fee.source.toLowerCase(),
    fee.type.toLowerCase(),
    fee.destination ? fee.destination.toLowerCase() : null,
    fee.product ? fee.product.toLowerCase() : null,
    conciergeMode ? 'concierge' : null,
  ].filter((part): part is string => !!part);

  return `$${amount} - ${parts.join(', ')}`;
}

/**
 * Order item payload for the BT service fee, appended to `orderPayload.items[]` on a custom-offer
 * order submission. Mirrors the shape the customer portal sends
 * (see [orderCreationSelectors.ts in www-le-business-traveller](../../../www-le-business-traveller/src/checkout/selectors/payment/orderCreationSelectors.ts)).
 *
 * Returns `null` when no fee was selected (or amount is 0). Caller appends the item only when non-null.
 */
export function buildServiceFeeOrderItem(
  paymentMetadata: App.CustomOfferPaymentMetadata,
): App.ServiceFeeOrderItemPayload | null {
  const fee = paymentMetadata.lebt_service_fee;
  if (!fee || fee.amount <= 0) {
    return null;
  }

  return {
    type: 'service_fee',
    transaction_key: uuidv4(),
    serviceFeeType: 'fixed_amount',
    serviceFeeSource: fee.source,
    serviceFeeChargeType: fee.charge_type,
    serviceFeeDestination: fee.destination,
    serviceFeeProduct: fee.product,
    conciergeMode: fee.concierge_mode,
    serviceFeeAmount: fee.amount,
  };
}

function buildDropdownOption({
  fee,
  conciergeMode,
}: {
  fee: App.BusinessServiceFee;
  conciergeMode: boolean;
}): CustomOfferServiceFeeDropdownOption {
  const amount = conciergeMode ? fee.conciergeAmount : fee.amount;
  return {
    source: fee.source,
    type: fee.type,
    destination: fee.destination,
    product: fee.product,
    conciergeMode,
    amount,
    label: formatServiceFeeLabel(fee, amount, conciergeMode),
  };
}
